import getSymbolFromCurrency from 'currency-symbol-map';

import { getNumberOfNights } from '@/lib/dates';

/**
 * Prices arrive from the platform as "floyd" integers: the amount multiplied by
 * 10,000, so £123.45 is 1234500. Everything the guest sees goes through
 * `fromFloyd` first.
 */
export const FLOYDIAN_FACTOR = 10000;

export const fromFloyd = (floydPrice: string | number) =>
	(Number(floydPrice) || 0) / FLOYDIAN_FACTOR;

export const toFloyd = (amount: number) => Math.round(amount * FLOYDIAN_FACTOR);

/** `£` for GBP, `€` for EUR and so on, falling back to the code itself. */
export const getCurrencySymbol = (currency: string) =>
	getSymbolFromCurrency(currency) ?? currency;

/** `£1,234.50`: a money amount with its symbol, always two decimals. */
export const formatMoney = (
	amount: number,
	currency: string,
	locale = 'en-GB',
) =>
	`${getCurrencySymbol(currency)}${new Intl.NumberFormat(locale, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(Math.abs(amount))}`;

/**
 * The average nightly price for a stay, rounded up to the nearest whole unit,
 * and the number of nights it covers. Returns zeros when either date is missing.
 */
export const calculatePricePerNight = (
	checkIn: string | undefined,
	checkOut: string | undefined,
	totalPrice: number,
): [pricePerNight: number, numberOfNights: number] => {
	if (!checkIn || !checkOut) {
		return [0, 0];
	}
	const numberOfNights = getNumberOfNights(checkIn, checkOut);
	if (numberOfNights <= 0) {
		return [0, 0];
	}
	return [Math.ceil(totalPrice / numberOfNights), numberOfNights];
};
